import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  type TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { NODE_COLORS } from '../../utils/workflowNodeHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

export type SparklineColorKey =
  | 'primary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info';

const COLOR_MAP: Record<SparklineColorKey, string> = {
  primary: NODE_COLORS.primary,
  success: NODE_COLORS.success,
  warning: NODE_COLORS.warning,
  destructive: NODE_COLORS.destructive,
  info: NODE_COLORS.info,
};

interface SparklineChartProps {
  /** Date string -> value (e.g. { "2/13/2026": 4, "2/14/2026": 1 }) */
  data: Record<string, number>;
  color?: SparklineColorKey;
  /** Chart height in pixels (default 88) */
  height?: number;
  /** If true, show values as percentage of total in tooltip */
  showAsPercentage?: boolean;
  total?: number;
}

/**
 * Minimal sparkline for campaign analytics cards.
 * Sorts dates and renders a single blue line with no axes/grid.
 */
const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  color = 'primary',
  height = 88,
  showAsPercentage = false,
  total = 1,
}) => {
  const chartData = useMemo(() => {
    const entries = Object.entries(data);
    if (entries.length === 0)
      return { labels: [] as string[], values: [] as number[] };

    const sorted = [...entries].sort((a, b) => {
      const dA = new Date(a[0]).getTime();
      const dB = new Date(b[0]).getTime();
      return dA - dB;
    });

    const labels = sorted.map(([dateStr]) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    const values = sorted.map(([, v]) => v);
    return { labels, values };
  }, [data]);

  const lineColor = COLOR_MAP[color] ?? COLOR_MAP.primary;

  const config = useMemo(
    () => ({
      labels: chartData.labels,
      datasets: [
        {
          label: 'Value',
          data: chartData.values,
          borderColor: lineColor,
          backgroundColor: `${lineColor}20`,
          borderWidth: 2,
          fill: true,
          tension: 0.2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBackgroundColor: lineColor,
          pointBorderColor: lineColor,
        },
      ],
    }),
    [chartData.labels, chartData.values, lineColor]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: (context: TooltipItem<'line'>) => {
              const value = Number(context.raw);
              if (showAsPercentage && total > 0)
                return `${((value / total) * 100).toFixed(1)}%`;
              return String(value);
            },
          },
        },
      },
      scales: {
        x: {
          display: false,
        },
        y: {
          display: false,
          beginAtZero: true,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
    }),
    [showAsPercentage, total]
  );

  if (chartData.labels.length === 0) return null;

  return (
    <div style={{ height }} className="w-full min-w-0">
      <Line data={config} options={options} />
    </div>
  );
};

export default SparklineChart;
